import Link from 'found/Link';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { createFragmentContainer, graphql } from 'react-relay';
import {
  Card, CardHeader, Col, Row, Table,
} from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import BadgeTradeRuleState from 'src/components/Badge/BadgeTradeRuleState';
import BadgeTradeType from 'src/components/Badge/BadgeTradeType';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import { APIConfig } from 'src/config';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import { PLATFORM_MODE_REBATE } from 'src/util/constants';
import { useIntlCurrency } from 'src/util/i18n/currency';
import displayUserName from 'src/util/i18n/decorators/displayUserName';
import { valueRangePerUnitWithStrong } from 'src/util/i18n/energyPrice';
import { getPriceRange } from 'src/util/tradeRule';

import AdminTradeRuleShowTrader from './AdminTradeRuleShowTrader';

/**
 * Description
 * @param {object} props
 * @returns {React.ReactElement} - AdminTradeRuleShow component
 */
function AdminTradeRuleShow(props) {
  if (!props) {
    return <Loading />;
  }

  const { rule } = props;
  const {
    uuid, tradeType, start, finish,
    state, priority, sequenceId, buyer,
    seller, proposedAt, proposedBy, cancelledAt,
    cancelledBy, acceptedAt, acceptedBy, rejectedAt,
    rejectedBy, closedAt, closedBy,
  } = rule;

  const priceRange = getPriceRange(rule);
  const intlCurrency = useIntlCurrency();
  const intl = useIntl();

  // Page title
  const pageTitle = intl.formatMessage(
    { id: 'admin.trade_rule.show.page_title', defaultMessage: 'Enosi Admin - Trade Rule - {uuid}' },
    { uuid },
  );

  // Breadcrumbs
  const breadcrumbAdmin = intl.formatMessage({ id: 'admin.breadcrumb.admin', defaultMessage: 'Admin' });
  const breadcrumbTradeRules = intl.formatMessage({ id: 'admin.breadcrumb.trade_rules', defaultMessage: 'Trade Rules' });
  const breadcrumbTradeRule = intl.formatMessage(
    { id: 'admin.trade_rule.show.breadcrumb.trade_rule', defaultMessage: 'Trade Rule: {uuid}' },
    { uuid },
  );

  // Card headers
  const tradeRuleDetailsHeader = intl.formatMessage({ id: 'admin.trade_rule.show.card.details', defaultMessage: 'Trade rule details' });
  const buyerHeader = intl.formatMessage({ id: 'admin.trade_rule.show.card.buyer', defaultMessage: 'Buyer' });
  const sellerHeader = intl.formatMessage({ id: 'admin.trade_rule.show.card.seller', defaultMessage: 'Seller' });
  const statesHeader = intl.formatMessage({ id: 'admin.trade_rule.show.card.states', defaultMessage: 'States' });

  // Table headers and labels
  const activeStatusLabel = intl.formatMessage({ id: 'admin.trade_rule.show.label.active_status', defaultMessage: 'Active status' });
  const idLabel = intl.formatMessage({ id: 'admin.trade_rule.show.label.id', defaultMessage: 'ID' });
  const startLabel = intl.formatMessage({ id: 'admin.trade_rule.show.label.start', defaultMessage: 'Start' });
  const finishLabel = intl.formatMessage({ id: 'admin.trade_rule.show.label.finish', defaultMessage: 'Finish' });
  const typeLabel = intl.formatMessage({ id: 'admin.trade_rule.show.label.type', defaultMessage: 'Type' });
  const stateLabel = intl.formatMessage({ id: 'admin.trade_rule.show.label.state', defaultMessage: 'State' });
  const priorityLabel = intl.formatMessage({ id: 'admin.trade_rule.show.label.priority', defaultMessage: 'Priority' });
  const sequenceIdLabel = intl.formatMessage({ id: 'admin.trade_rule.show.label.sequence_id', defaultMessage: 'Sequence ID' });

  // States table headers
  const proposedHeader = intl.formatMessage({ id: 'admin.trade_rule.show.states.proposed', defaultMessage: 'Proposed' });
  const cancelledHeader = intl.formatMessage({ id: 'admin.trade_rule.show.states.cancelled', defaultMessage: 'Cancelled' });
  const acceptedHeader = intl.formatMessage({ id: 'admin.trade_rule.show.states.accepted', defaultMessage: 'Accepted' });
  const rejectedHeader = intl.formatMessage({ id: 'admin.trade_rule.show.states.rejected', defaultMessage: 'Rejected' });
  const closedHeader = intl.formatMessage({ id: 'admin.trade_rule.show.states.closed', defaultMessage: 'Closed' });

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbTradeRules, path: '/admin/trade-rules' },
          { name: breadcrumbTradeRule },
        ]}
      />

      <h1>
        {uuid}
      </h1>

      <Row className="clear-both">
        <Col xs={12} sm={6} md={4}>
          <Card>
            <CardHeader>
              {tradeRuleDetailsHeader}
            </CardHeader>
            <Table responsive>
              <tbody>
                <tr>
                  <th scope="row" className="text-right">{activeStatusLabel}</th>
                  <td>
                    <BadgeActive start={start} finish={finish} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{idLabel}</th>
                  <td>
                    <UUID uuid={uuid} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{startLabel}</th>
                  <td>
                    {start && DateTime.fromSeconds(start).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{finishLabel}</th>
                  <td>
                    {finish && DateTime.fromSeconds(finish).toLocaleString(DateTime.DATETIME_FULL)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{typeLabel}</th>
                  <td>
                    <BadgeTradeType type={tradeType} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{stateLabel}</th>
                  <td>
                    <BadgeTradeRuleState state={state} />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">
                    {APIConfig().MODE === PLATFORM_MODE_REBATE ? 'Discount' : 'Price'}
                  </th>
                  <td>
                    {valueRangePerUnitWithStrong(intl, intlCurrency, priceRange)}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{priorityLabel}</th>
                  <td>
                    {priority}
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="text-right">{sequenceIdLabel}</th>
                  <td>
                    {sequenceId}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>

        <Col xs={12} sm={6} md={8}>
          <Card className="mb-4">
            <CardHeader>
              {buyerHeader}
            </CardHeader>
            <AdminTradeRuleShowTrader trader={buyer} />
          </Card>

          <Card className="mb-4">
            <CardHeader>
              {sellerHeader}
            </CardHeader>
            <AdminTradeRuleShowTrader trader={seller} />
          </Card>

          <Card className="mb-4">
            <CardHeader>
              {statesHeader}
            </CardHeader>
            <Table responsive>
              <thead>
                <tr>
                  <th scope="col">{proposedHeader}</th>
                  <th scope="col">{cancelledHeader}</th>
                  <th scope="col">{acceptedHeader}</th>
                  <th scope="col">{rejectedHeader}</th>
                  <th scope="col">{closedHeader}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    {proposedAt
                      && DateTime.fromSeconds(proposedAt).toLocaleString(DateTime.DATETIME_FULL)}
                    <br />
                    {proposedBy && (
                      <>
                        <Link to={`/admin/users/${proposedBy.uuid}`}>
                          {displayUserName(intl, proposedBy)}
                        </Link>
                        <br />
                        {proposedBy.email}
                        <br />
                        <BadgeActive
                          start={proposedBy.active.start}
                          finish={proposedBy.active.finish}
                        />
                      </>
                    )}
                  </td>
                  <td>
                    {cancelledAt
                      && DateTime.fromSeconds(cancelledAt).toLocaleString(DateTime.DATETIME_FULL)}
                    <br />
                    {cancelledBy && (
                      <>
                        <Link to={`/admin/users/${cancelledBy.uuid}`}>
                          {displayUserName(intl, cancelledBy)}
                        </Link>
                        <br />
                        {cancelledBy.email}
                        <br />
                        <BadgeActive
                          start={cancelledBy.active.start}
                          finish={cancelledBy.active.finish}
                        />
                      </>
                    )}
                  </td>
                  <td>
                    {acceptedAt
                      && DateTime.fromSeconds(acceptedAt).toLocaleString(DateTime.DATETIME_FULL)}
                    <br />
                    {acceptedBy && (
                      <>
                        <Link to={`/admin/users/${acceptedBy.uuid}`}>
                          {displayUserName(intl, acceptedBy)}
                        </Link>
                        <br />
                        {acceptedBy.email}
                        <br />
                        <BadgeActive
                          start={acceptedBy.active.start}
                          finish={acceptedBy.active.finish}
                        />
                      </>
                    )}
                  </td>
                  <td>
                    {rejectedAt
                      && DateTime.fromSeconds(rejectedAt).toLocaleString(DateTime.DATETIME_FULL)}
                    <br />
                    {rejectedBy && (
                      <>
                        <Link to={`/admin/users/${rejectedBy.uuid}`}>
                          {displayUserName(intl, rejectedBy)}
                        </Link>
                        <br />
                        {rejectedBy.email}
                        <br />
                        <BadgeActive
                          start={rejectedBy.active.start}
                          finish={rejectedBy.active.finish}
                        />
                      </>
                    )}
                  </td>
                  <td>
                    {closedAt
                      && DateTime.fromSeconds(closedAt).toLocaleString(DateTime.DATETIME_FULL)}
                    <br />
                    {closedBy && (
                      <>
                        <Link to={`/admin/users/${closedBy.uuid}`}>
                          {displayUserName(intl, closedBy)}
                        </Link>
                        <br />
                        {closedBy.email}
                        <br />
                        <BadgeActive
                          start={closedBy.active.start}
                          finish={closedBy.active.finish}
                        />
                      </>
                    )}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </>
  );
}

AdminTradeRuleShow.propTypes = {
  relay: PropTypes.shape({
    hasMore: PropTypes.func,
    isLoading: PropTypes.func,
    loadMore: PropTypes.func,
  }).isRequired,
  rule: PropTypes.shape({
    uuid: PropTypes.string,
    tradeType: PropTypes.string,
    clauses: PropTypes.shape({
      edges: PropTypes.arrayOf(
        PropTypes.shape({
          node: PropTypes.shape({
            price: PropTypes.number,
          }),
        }),
      ),
    }),
    start: PropTypes.number,
    finish: PropTypes.number,
    state: PropTypes.string,
    priority: PropTypes.number,
    sequenceId: PropTypes.number,
    buyer: PropTypes.shape({
      tradePoint: PropTypes.shape({
        uuid: PropTypes.string,
        type: PropTypes.string,
      }),
      community: PropTypes.shape({
        uuid: PropTypes.string,
        title: PropTypes.string,
        active: PropTypes.shape({
          start: PropTypes.number,
          finish: PropTypes.number,
        }),
      }),
      user: PropTypes.shape({
        uuid: PropTypes.string,
        email: PropTypes.string,
        givenName: PropTypes.string,
        familyName: PropTypes.string,
        active: PropTypes.shape({
          start: PropTypes.number,
          finish: PropTypes.number,
        }),
      }),
    }),
    seller: PropTypes.shape({
      tradePoint: PropTypes.shape({
        uuid: PropTypes.string,
        type: PropTypes.string,
      }),
      community: PropTypes.shape({
        uuid: PropTypes.string,
        title: PropTypes.string,
        active: PropTypes.shape({
          start: PropTypes.number,
          finish: PropTypes.number,
        }),
      }),
      user: PropTypes.shape({
        uuid: PropTypes.string,
        email: PropTypes.string,
        givenName: PropTypes.string,
        familyName: PropTypes.string,
        active: PropTypes.shape({
          start: PropTypes.number,
          finish: PropTypes.number,
        }),
      }),
    }),
    proposedAt: PropTypes.number,
    proposedBy: PropTypes.shape({
      uuid: PropTypes.string,
      email: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
      active: PropTypes.shape({
        start: PropTypes.number,
        finish: PropTypes.number,
      }),
    }),
    cancelledAt: PropTypes.number,
    cancelledBy: PropTypes.shape({
      uuid: PropTypes.string,
      email: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
      active: PropTypes.shape({
        start: PropTypes.number,
        finish: PropTypes.number,
      }),
    }),
    acceptedAt: PropTypes.number,
    acceptedBy: PropTypes.shape({
      uuid: PropTypes.string,
      email: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
      active: PropTypes.shape({
        start: PropTypes.number,
        finish: PropTypes.number,
      }),
    }),
    rejectedAt: PropTypes.number,
    rejectedBy: PropTypes.shape({
      uuid: PropTypes.string,
      email: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
      active: PropTypes.shape({
        start: PropTypes.number,
        finish: PropTypes.number,
      }),
    }),
    closedAt: PropTypes.number,
    closedBy: PropTypes.shape({
      uuid: PropTypes.string,
      email: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
      active: PropTypes.shape({
        start: PropTypes.number,
        finish: PropTypes.number,
      }),
    }),
  }).isRequired,
};

export default createFragmentContainer(
  AdminTradeRuleShow,
  {
    rule: graphql`
      fragment AdminTradeRuleShow_rule on TradeRule {
        uuid
        tradeType
        clauses {
          edges {
            node {
              price
            }
          }
        }
        start
        finish
        state
        priority
        sequenceId
        buyer {
          tradePoint { uuid type }
          community {
            uuid
            title
            active { start finish }
          }
          user {
            uuid
            email
            givenName
            familyName
            active { start finish }
          }
        }
        seller {
          tradePoint { uuid type }
          community {
            uuid
            title
            active { start finish }
          }
          user {
            uuid
            email
            givenName
            familyName
            active { start finish }
          }
        }
        proposedAt
        proposedBy {
          uuid
          email
          givenName
          familyName
          active { start finish }
        }
        cancelledAt
        cancelledBy {
          uuid
          email
          givenName
          familyName
          active { start finish }
        }
        acceptedAt
        acceptedBy {
          uuid
          email
          givenName
          familyName
          active { start finish }
        }
        rejectedAt
        rejectedBy {
          uuid
          email
          givenName
          familyName
          active { start finish }
        }
        closedAt
        closedBy {
          uuid
          email
          givenName
          familyName
          active { start finish }
        }
      }
    `,
  },
);
